import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'motion/react';
import { ArrowLeft, ArrowRight, TrendingUp, CheckCircle, ExternalLink } from 'lucide-react';
import Layout from '../components/layout/Layout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Text Reveal Animation
const TextReveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

const CaseStudyDetail = () => {
  const { id } = useParams();
  const [study, setStudy] = useState(null);
  const [relatedStudies, setRelatedStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 150]);

  useEffect(() => {
    fetchStudy();
    fetchRelatedStudies();
  }, [id]);

  const fetchStudy = async () => {
    try {
      const response = await fetch(`${API_URL}/api/case-studies/${id}`);
      if (response.ok) {
        const data = await response.json();
        setStudy(data);
      }
    } catch (error) {
      console.error('Error fetching case study:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedStudies = async () => {
    try {
      const response = await fetch(`${API_URL}/api/case-studies`);
      if (response.ok) {
        const data = await response.json();
        setRelatedStudies(data.filter(s => s.id !== id).slice(0, 3));
      }
    } catch (error) {
      console.error('Error fetching related studies:', error);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
          <motion.div
            className="w-12 h-12 border-2 border-[#FF6B35]/30 border-t-[#FF6B35] rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
        </div>
      </Layout>
    );
  }

  if (!study) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#FFFBF5] flex flex-col items-center justify-center">
          <h1 className="font-display text-4xl text-[#2D2D2D] mb-4">Case Study Not Found</h1>
          <p className="text-[#5A5A5A] mb-8">The case study you're looking for doesn't exist.</p>
          <Link to="/case-studies" className="btn-sunrise">
            <span className="flex items-center gap-2">
              <ArrowLeft size={18} />
              Back to Case Studies
            </span>
          </Link>
        </div>
      </Layout>
    );
  }

  // Generate detailed content based on the case study
  const generateDetailedContent = () => {
    const { client, title, description, category, results, services } = study;
    
    return {
      challenge: `When ${client} first approached us, they were facing significant challenges in their market. Despite having a quality product/service, they struggled to reach their target audience effectively. Their existing digital presence was underperforming, and they needed a comprehensive strategy to transform their business outcomes. The competitive landscape was tough, and they needed to stand out in a crowded marketplace.`,
      
      approach: `Our team conducted an in-depth analysis of ${client}'s business, target audience, and competitive landscape. We developed a tailored strategy that leveraged our expertise in ${services.join(', ')}. The approach was multi-faceted, combining data-driven insights with creative execution. We focused on building a sustainable growth engine rather than quick wins, ensuring long-term success for the brand.`,
      
      execution: `The implementation phase was structured into three key stages:

Phase 1: Foundation Building
We started by establishing the core infrastructure needed for success. This included comprehensive market research, audience segmentation, and competitive analysis. We identified key opportunities and developed a roadmap for execution.

Phase 2: Strategic Implementation
With the foundation in place, we rolled out our ${category.toLowerCase()} strategy. Each element was carefully crafted to align with ${client}'s brand values while maximizing impact. We continuously monitored performance and optimized in real-time.

Phase 3: Scale and Optimization
As results started flowing in, we focused on scaling successful initiatives while eliminating underperformers. This agile approach allowed us to maximize ROI and achieve exceptional results.`,
      
      results: `The results exceeded expectations across all key metrics. ${results.map(r => `We achieved ${r.value} in ${r.label}`).join('. ')}. Most importantly, these weren't just numbers—they translated into real business growth and transformed how ${client} operates in their market.`,
      
      testimonial: `"Working with Buzz Craft Maven was transformative for our business. Their strategic approach and execution excellence helped us achieve results we didn't think were possible. The team truly understood our vision and delivered beyond our expectations."`,
      
      keyTakeaways: [
        `Strategic planning is essential for sustainable growth`,
        `Data-driven decisions lead to better outcomes`,
        `Consistent execution beats sporadic efforts`,
        `The right partner can accelerate your success`,
        `Integrated approaches yield the best results`
      ]
    };
  };

  const content = generateDetailedContent();

  return (
    <Layout>
      {/* HERO SECTION */}
      <section ref={heroRef} className="relative min-h-[70vh] bg-sunrise-hero overflow-hidden flex items-end">
        {/* Background Image */}
        <motion.div 
          className="absolute inset-0"
          style={{ y: heroY }}
        >
          <img
            src={study.image}
            alt={study.client}
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FFFBF5] via-[#FFFBF5]/80 to-transparent" />
        </motion.div>

        {/* Sun Glow Effect */}
        <div className="absolute top-20 right-[20%] w-[400px] h-[400px] sun-orb sun-orb-primary opacity-30" />

        {/* Content */}
        <div className="container mx-auto px-6 md:px-12 py-16 relative z-10">
          {/* Back Link */}
          <Link 
            to="/case-studies" 
            className="inline-flex items-center gap-2 text-[#5A5A5A] hover:text-[#FF6B35] transition-colors mb-8"
          >
            <ArrowLeft size={18} />
            Back to Case Studies
          </Link>

          <div className="max-w-4xl">
            {/* Category & Date */}
            <TextReveal>
              <div className="flex items-center gap-4 mb-6">
                <span className="px-4 py-1.5 bg-gradient-to-r from-[#FF6B35] to-[#FFB627] text-white text-sm font-semibold rounded-full">
                  {study.category}
                </span>
                <span className="text-[#5A5A5A]">{study.date}</span>
              </div>
            </TextReveal>

            {/* Client & Title */}
            <TextReveal delay={0.1}>
              <p className="text-[#FF6B35] text-lg font-medium mb-2">{study.client}</p>
              <h1 className="font-display text-3xl md:text-4xl lg:text-5xl text-[#2D2D2D] leading-tight mb-6">
                {study.title}
              </h1>
            </TextReveal>

            <TextReveal delay={0.2}>
              <p className="text-lg text-[#5A5A5A] max-w-3xl">
                {study.description}
              </p>
            </TextReveal>
          </div>
        </div>
      </section>

      {/* RESULTS BANNER */}
      <section className="py-8 bg-white border-y border-[#FF6B35]/10">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            {study.results.map((result, index) => (
              <TextReveal key={result.label} delay={index * 0.1}>
                <div className="text-center">
                  <p className="font-display text-4xl md:text-5xl font-bold text-[#FF6B35]">{result.value}</p>
                  <p className="text-[#5A5A5A] text-sm uppercase tracking-widest mt-1">{result.label}</p>
                </div>
              </TextReveal>
            ))}
          </div>
        </div>
      </section>

      {/* DETAILED CONTENT */}
      <section className="py-16 md:py-24 bg-[#FFFBF5]">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            {/* The Challenge */}
            <TextReveal>
              <div className="mb-16">
                <h2 className="font-display text-2xl md:text-3xl text-[#2D2D2D] mb-6">
                  The <span className="text-gradient-sunrise">Challenge</span>
                </h2>
                <p className="text-lg text-[#5A5A5A] leading-relaxed">
                  {content.challenge}
                </p>
              </div>
            </TextReveal>

            {/* Our Approach */}
            <TextReveal>
              <div className="mb-16">
                <h2 className="font-display text-2xl md:text-3xl text-[#2D2D2D] mb-6">
                  Our <span className="text-gradient-sunrise">Approach</span>
                </h2>
                <p className="text-lg text-[#5A5A5A] leading-relaxed">
                  {content.approach}
                </p>
              </div>
            </TextReveal>

            {/* Services Used */}
            <TextReveal>
              <div className="mb-16 p-8 bg-white rounded-2xl border border-[#FF6B35]/10">
                <h3 className="font-display text-xl text-[#2D2D2D] mb-6">Services Deployed</h3>
                <div className="flex flex-wrap gap-3">
                  {study.services.map((service) => (
                    <span
                      key={service}
                      className="px-4 py-2 bg-gradient-to-r from-[#FF6B35]/10 to-[#FFB627]/10 text-[#FF6B35] rounded-full font-medium"
                    >
                      {service}
                    </span>
                  ))}
                </div>
              </div>
            </TextReveal>

            {/* Execution */}
            <TextReveal>
              <div className="mb-16">
                <h2 className="font-display text-2xl md:text-3xl text-[#2D2D2D] mb-6">
                  The <span className="text-gradient-sunrise">Execution</span>
                </h2>
                <div className="text-lg text-[#5A5A5A] leading-relaxed space-y-6">
                  {content.execution.split('\n\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </div>
            </TextReveal>

            {/* Results */}
            <TextReveal>
              <div className="mb-16">
                <h2 className="font-display text-2xl md:text-3xl text-[#2D2D2D] mb-6">
                  The <span className="text-gradient-sunrise">Results</span>
                </h2>
                <p className="text-lg text-[#5A5A5A] leading-relaxed">
                  {content.results}
                </p>
              </div>
            </TextReveal>

            {/* Testimonial */}
            <TextReveal>
              <div className="mb-16 p-8 md:p-12 bg-gradient-to-br from-[#FF6B35]/10 to-[#FFB627]/10 rounded-2xl border border-[#FF6B35]/20">
                <p className="text-xl md:text-2xl text-[#2D2D2D] italic leading-relaxed mb-6">
                  {content.testimonial}
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FFB627] flex items-center justify-center text-white font-bold">
                    {study.client.charAt(0)}
                  </div>
                  <div>
                    <p className="text-[#2D2D2D] font-semibold">{study.client}</p>
                    <p className="text-[#5A5A5A] text-sm">Client Partner</p>
                  </div>
                </div>
              </div>
            </TextReveal>

            {/* Key Takeaways */}
            <TextReveal>
              <div className="mb-16">
                <h2 className="font-display text-2xl md:text-3xl text-[#2D2D2D] mb-6">
                  Key <span className="text-gradient-sunrise">Takeaways</span>
                </h2>
                <div className="space-y-4">
                  {content.keyTakeaways.map((takeaway, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="text-[#FF6B35] flex-shrink-0 mt-1" size={20} />
                      <p className="text-lg text-[#5A5A5A]">{takeaway}</p>
                    </div>
                  ))}
                </div>
              </div>
            </TextReveal>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <TextReveal>
              <h2 className="font-display text-3xl md:text-4xl text-[#2D2D2D] mb-4">
                Ready to achieve similar <span className="text-gradient-sunrise">results?</span>
              </h2>
              <p className="text-lg text-[#5A5A5A] mb-8 max-w-2xl mx-auto">
                Let's discuss how we can help transform your business with our proven strategies and expertise.
              </p>
              <Link to="/contact">
                <motion.button
                  className="btn-sunrise px-8 py-4 text-lg"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Start Your Project
                  <ArrowRight size={20} className="ml-2" />
                </motion.button>
              </Link>
            </TextReveal>
          </div>
        </div>
      </section>

      {/* RELATED CASE STUDIES */}
      {relatedStudies.length > 0 && (
        <section className="section-padding bg-[#FFFBF5]">
          <div className="container mx-auto px-6 md:px-12">
            <TextReveal>
              <div className="flex items-center justify-between mb-12">
                <h2 className="font-display text-3xl md:text-4xl text-[#2D2D2D]">
                  More <span className="text-gradient-sunrise">Success Stories</span>
                </h2>
                <Link to="/case-studies" className="btn-ghost-warm">
                  View All
                  <ArrowRight size={16} />
                </Link>
              </div>
            </TextReveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedStudies.map((relatedStudy, index) => (
                <TextReveal key={relatedStudy.id} delay={index * 0.1}>
                  <Link to={`/case-studies/${relatedStudy.id}`}>
                    <motion.article
                      className="card-fresh overflow-hidden group"
                      whileHover={{ y: -8 }}
                    >
                      <div className="relative h-48 -mx-8 -mt-8 mb-4 overflow-hidden">
                        <img
                          src={relatedStudy.image}
                          alt={relatedStudy.client}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
                        <span className="absolute top-4 left-4 px-3 py-1 bg-gradient-to-r from-[#FF6B35] to-[#FFB627] text-white text-xs font-semibold rounded-full">
                          {relatedStudy.category}
                        </span>
                      </div>
                      <p className="text-[#FF6B35] text-sm font-medium mb-2">{relatedStudy.client}</p>
                      <h3 className="font-display text-lg text-[#2D2D2D] group-hover:text-[#FF6B35] transition-colors line-clamp-2">
                        {relatedStudy.title}
                      </h3>
                    </motion.article>
                  </Link>
                </TextReveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default CaseStudyDetail;
